import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { TokenStorage } from '../../utils/api';
import AppShell from '../layout/AppShell';
import { SkeletonBillingCard } from '../common/SkeletonLoader';

function formatDateOnly(value, timeZone) {
  try {
    let ms;
    if (typeof value === 'number') {
      ms = value < 1e12 ? value * 1000 : value;
    } else if (value instanceof Date) {
      ms = value.getTime();
    } else if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (Number.isNaN(parsed)) return '—';
      ms = parsed;
    } else {
      return '—';
    }
    const dt = new Date(ms);
    const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return new Intl.DateTimeFormat(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: tz,
    }).format(dt);
  } catch {
    return '—';
  }
}

export default function BillingPanel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showUpgradeBanner = searchParams.get('upgrade') === 'planner';
  const returnTo = searchParams.get('from');

  // Clean upgrade params from URL once loaded (cosmetic, doesn't affect state)
  useEffect(() => {
    if (showUpgradeBanner) {
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete('upgrade');
      cleaned.delete('from');
      const qs = cleaned.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleLogout = async () => {
    try {
      const useCookieFlow = localStorage.getItem('auth_mode') === 'cookie';
      const csrfToken = localStorage.getItem('csrfToken');
      if (useCookieFlow) {
        await api.post('/api/auth/logout', {}, { headers: { 'X-CSRF-Token': csrfToken }, withCredentials: true });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      TokenStorage.removeToken();
      navigate('/logged-out', { replace: true });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, plansRes] = await Promise.allSettled([
          api.get('/api/subscriptions/summary'),
          api.get('/api/subscriptions/plans'),
        ]);

        if (summaryRes.status === 'fulfilled') {
          const data = summaryRes.value.data;
          setSummary(data);

          // If user arrived via entitlement redirect but already has an active plan
          // (e.g. returning from Stripe portal after subscribing), redirect back
          if (returnTo && data?.plan?.priceId) {
            navigate(decodeURIComponent(returnTo), { replace: true });
            return;
          }
        }

        if (plansRes.status === 'fulfilled') {
          setPlans(plansRes.value.data?.plans || []);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [returnTo, navigate]);

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      const { data } = await api.post('/api/subscriptions/portal');
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      console.error('Failed to open portal:', err);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formatPrice = (cents, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <AppShell onLogout={handleLogout}>
        <div className="space-y-4 sm:space-y-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Plan & Billing</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your subscription and payment details</p>
          </div>
          <SkeletonBillingCard />
          <SkeletonBillingCard />
          <div className="h-20 bg-gray-100 rounded-lg animate-pulse"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="h-48 bg-gray-100 rounded-lg animate-pulse"></div>
            <div className="h-48 bg-gray-100 rounded-lg animate-pulse"></div>
            <div className="h-48 bg-gray-100 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </AppShell>
    );
  }

  const currentPlanId = summary?.plan?.priceId;
  const hasActivePlan = !!currentPlanId;
  const status = summary?.status;
  const isTrialing = status === 'trialing';
  const isPastDue = status === 'past_due';

  return (
    <AppShell onLogout={handleLogout}>
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Plan & Billing</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your subscription and payment details</p>
        </div>

        {showUpgradeBanner && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm font-medium text-amber-800">
              A subscription is required to access the Cashflow Planner. Choose a plan below to get started.
            </p>
          </div>
        )}

        {/* Current Plan Section */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0 hidden sm:block">
              <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Current Plan</h2>
                {hasActivePlan && !isPastDue && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    isTrialing ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {isTrialing ? 'Trial' : 'Active'}
                  </span>
                )}
                {isPastDue && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                    Payment retry in progress
                  </span>
                )}
              </div>

              {hasActivePlan ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                      Plan:
                    </label>
                    <p className="text-gray-900 text-lg font-medium">
                      {summary.plan.nickname || 'STB Pro'}
                    </p>
                  </div>

                  {summary.plan.interval && (
                    <div>
                      <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                        Billed:
                      </label>
                      <p className="text-gray-900">
                        {summary.plan.interval === 'year' ? 'Annually' : 'Monthly'}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                      Active notifications:
                    </label>
                    <p className="text-gray-900">
                      {summary.enabledSchedules || 0}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-gray-600">
                  <p>No active subscription plan</p>
                  <p className="text-sm text-amber-600 mt-2">
                    Subscribe to access the Cashflow Planner, SMS alerts, and the 8-week forecast.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Billing Information Section */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0 hidden sm:block">
              <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Billing Information</h2>
              
              {hasActivePlan && summary.nextInvoiceDate ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                      Next Billing Date:
                    </label>
                    <p className="text-gray-900">
                      {formatDateOnly(summary.nextInvoiceDate, userTimeZone)}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Your subscription will automatically renew on this date
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-gray-600">
                  <p>No billing information available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Manage Subscription Button */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Your Subscription</h3>
              <p className="text-sm text-gray-600">
                Update payment method, change plan, view invoices, or cancel subscription
              </p>
            </div>
            <button
              onClick={openPortal}
              disabled={portalLoading}
              className="w-full sm:w-auto px-6 py-3 min-h-[44px] sm:min-h-0 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          </div>
        </div>

        {/* Available Plans Section */}
        {plans.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Available Plans</h2>
            <p className="text-sm text-gray-600 mb-6">
              Choose the plan that best fits your needs. You can change plans anytime through the billing portal.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const isCurrentPlan = plan.id === currentPlanId;
                
                return (
                  <div
                    key={plan.id}
                    className={`border rounded-lg p-4 sm:p-5 ${
                      isCurrentPlan 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {plan.nickname}
                      </h3>
                      {isCurrentPlan && (
                        <span className="text-xs px-2 py-1 bg-blue-600 text-white rounded-full font-medium">
                          Current
                        </span>
                      )}
                    </div>
                    
                    <div className="mb-4">
                      <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {formatPrice(plan.unit_amount, plan.currency)}
                      </div>
                      <div className="text-sm text-gray-600">
                        per {plan.interval}
                      </div>
                    </div>

                    {plan.features && plan.features.length > 0 && (
                      <div className="space-y-2">
                        {plan.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-700">
                            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {feature}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 text-center">
              <button
                onClick={openPortal}
                className="inline-flex items-center min-h-[44px] sm:min-h-0 text-blue-600 hover:text-blue-700 font-medium"
              >
                Change plan in billing portal →
              </button>
            </div>
          </div>

        )}
      </div>
    </AppShell>
  );
}